// ================================
// CONSTANTS
// ================================

const NON_NEGATIVE_FIELDS = new Set(['Vehicle mileage', 'MSRP', 'Down payment', 'Loan amount', 'Rebate', 'Due at signing', 'APR']);

// ================================
// VALIDATION HELPER FUNCTIONS
// ================================

/**
 * Generic helper to process validation results and update errors/coerced data
 * @param {Array} validations - Array of validation configurations
 * @param {Array} errors - Errors array to update
 * @param {Object} coerced - Coerced data object to update
 */
const processValidations = (validations, errors, coerced) => {
  validations.forEach(_ref => {
    let {
      field,
      value,
      validator
    } = _ref;
    const result = validator(value);
    if (result.error) {
      errors.push(result.error);
    } else if (result.coerced !== null && result.coerced !== undefined) {
      coerced[field] = result.coerced;
    }
  });
};

/**
 * Validates a required string field
 * @param {*} value - Value to validate
 * @param {string} fieldName - Name of the field for error messages
 * @returns {Object} Validation result with error and coerced value
 */
const validateRequiredString = (value, fieldName) => {
  if (!value || typeof value !== 'string' || !value.trim()) {
    return {
      error: `${fieldName} is required, validation failed - BOA will redirect to check-parameters page`,
      coerced: null
    };
  }
  return {
    error: null,
    coerced: value.trim()
  };
};

/**
 * Validates an optional string field
 * @param {*} value - Value to validate
 * @param {string} fieldName - Name of the field for error messages
 * @returns {Object} Validation result with error and coerced value
 */
const validateOptionalString = (value, fieldName) => {
  if (value === undefined || value === null || value === '') {
    return {
      error: null,
      coerced: undefined
    };
  }
  if (typeof value !== 'string') {
    return {
      error: `${fieldName} must be a string`,
      coerced: null
    };
  }
  return {
    error: null,
    coerced: value.trim()
  };
};

/**
 * Validates a required number field with various options
 * @param {*} value - Value to validate
 * @param {string} fieldName - Name of the field for error messages
 * @param {Object} options - Validation options (min, max, allowZero, requirePositive)
 * @returns {Object} Validation result with error and coerced value
 */
const validateRequiredNumber = function (value, fieldName) {
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  const {
    min = 0,
    max,
    allowZero = true,
    requirePositive = false
  } = options;
  const getErrorMessage = () => {
    if (requirePositive) {
      return `${fieldName} must be a valid positive number, validation failed - BOA will redirect to check-parameters page`;
    }
    if (NON_NEGATIVE_FIELDS.has(fieldName) && min === 0) {
      return `${fieldName} must be a valid non-negative number, validation failed - BOA will redirect to check-parameters page`;
    }
    return `${fieldName} must be a valid number, validation failed - BOA will redirect to check-parameters page`;
  };
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return {
      error: getErrorMessage(),
      coerced: null
    };
  }
  const numValue = Number(value);
  if (!allowZero && numValue === 0) {
    return {
      error: `${fieldName} must be greater than 0, validation failed - BOA will redirect to check-parameters page`,
      coerced: null
    };
  }
  if (requirePositive && numValue <= 0) {
    return {
      error: `${fieldName} must be a valid positive number, validation failed - BOA will redirect to check-parameters page`,
      coerced: null
    };
  }
  if (numValue < min || max !== undefined && numValue > max) {
    if (NON_NEGATIVE_FIELDS.has(fieldName) && min === 0) {
      return {
        error: `${fieldName} must be a valid non-negative number, validation failed - BOA will redirect to check-parameters page`,
        coerced: null
      };
    }
    if (numValue < min) {
      return {
        error: `${fieldName} must be at least ${min}, validation failed - BOA will redirect to check-parameters page`,
        coerced: null
      };
    }
    return {
      error: `${fieldName} must be at most ${max}, validation failed - BOA will redirect to check-parameters page`,
      coerced: null
    };
  }
  return {
    error: null,
    coerced: numValue
  };
};

/**
 * Validates an optional number field
 * @param {*} value - Value to validate
 * @param {string} fieldName - Name of the field for error messages
 * @param {Object} options - Validation options
 * @returns {Object} Validation result with error and coerced value
 */
const validateOptionalNumber = function (value, fieldName) {
  let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  if (value === undefined || value === null) {
    return {
      error: null,
      coerced: undefined
    };
  }
  return validateRequiredNumber(value, fieldName, options);
};

/**
 * Validates a vehicle year
 * @param {*} year - Year value to validate
 * @returns {Object} Validation result with error and coerced value
 */
const validateYear = year => {
  const currentYear = new Date().getFullYear();
  const validation = validateRequiredNumber(year, 'Vehicle year', {
    min: 1900,
    max: currentYear + 2
  });
  if (validation.error) {
    return {
      error: `Vehicle year must be a valid year between 1900 and ${currentYear + 2}, validation failed - BOA will redirect to check-parameters page`,
      coerced: null
    };
  }
  return {
    error: null,
    coerced: validation.coerced.toString()
  };
};

/**
 * Validates a VIN (Vehicle Identification Number)
 * @param {*} vin - VIN value to validate
 * @returns {Object} Validation result with error and coerced value
 */
const validateVin = vin => {
  if (!vin || typeof vin !== 'string' || vin.length !== 17 || !/^[A-HJ-NPR-Z0-9]{17}$/i.test(vin)) {
    return {
      error: 'VIN must be exactly 17 characters and contain only valid alphanumeric characters, validation failed - BOA will redirect to check-parameters page',
      coerced: null
    };
  }
  return {
    error: null,
    coerced: vin.toUpperCase()
  };
};

/**
 * Validates a US state code (2-letter abbreviation)
 * @param {*} state - State code to validate
 * @returns {Object} Validation result with error and coerced value
 */
const validateStateCode = state => {
  const validStates = new Set(['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC' // District of Columbia
  ]);
  if (!state || typeof state !== 'string') {
    return {
      error: 'State is required, validation failed - BOA will redirect to check-parameters page',
      coerced: null
    };
  }
  const normalizedState = state.trim().toUpperCase();
  if (normalizedState.length !== 2) {
    return {
      error: 'State must be a valid 2-letter US state code, validation failed - BOA will redirect to check-parameters page',
      coerced: null
    };
  }
  if (!validStates.has(normalizedState)) {
    return {
      error: 'State must be a valid US state code, validation failed - BOA will redirect to check-parameters page',
      coerced: null
    };
  }
  return {
    error: null,
    coerced: normalizedState
  };
};

/**
 * Validates a US ZIP code (5-digit format)
 * @param {*} zipCode - ZIP code to validate
 * @returns {Object} Validation result with error and coerced value
 */
const validateZipCode = zipCode => {
  if (!zipCode || typeof zipCode !== 'string') {
    return {
      error: 'ZIP code is required, validation failed - BOA will redirect to check-parameters page',
      coerced: null
    };
  }
  const normalizedZip = zipCode.trim();

  // Check for 5-digit ZIP code format
  if (!/^\d{5}$/.test(normalizedZip)) {
    return {
      error: 'ZIP code must be a valid 5-digit US postal code, validation failed - BOA will redirect to check-parameters page',
      coerced: null
    };
  }
  return {
    error: null,
    coerced: normalizedZip
  };
};

/**
 * Validates a photo URL
 * @param {*} photoUrl - Photo URL to validate
 * @returns {Object} Validation result with error and coerced value
 */
const validatePhotoUrl = photoUrl => {
  if (!photoUrl || typeof photoUrl !== 'string' || !photoUrl.trim() || !/^https?:\/\/.+\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(photoUrl)) {
    return {
      error: 'Photo URL must be a valid image URL (jpg, jpeg, png, gif, or webp), validation failed - BOA will redirect to check-parameters page',
      coerced: null
    };
  }
  return {
    error: null,
    coerced: photoUrl.trim()
  };
};

/**
 * Validates dealer information
 * @param {Object} dealerInfo - Dealer information to validate
 * @param {*} fallbackDealerId - Fallback dealer ID if not provided
 * @returns {Object} Validation result with errors and coerced data
 */
const validateDealerInfo = (dealerInfo, fallbackDealerId) => {
  const errors = [];
  const coerced = {};

  // Dealer ID has custom validation logic
  const dealerIdResult = (() => {
    const val = dealerInfo.dealerId || fallbackDealerId;
    if (!val || typeof val !== 'number' && typeof val !== 'string') {
      return {
        error: 'Dealer ID is required, validation failed - BOA will redirect to check-parameters page',
        coerced: null
      };
    }
    return {
      error: null,
      coerced: val
    };
  })();
  if (dealerIdResult.error) {
    errors.push(dealerIdResult.error);
  } else if (dealerIdResult.coerced !== null && dealerIdResult.coerced !== undefined) {
    coerced.dealerId = dealerIdResult.coerced;
  }

  // Validate string fields directly
  const validations = [{
    field: 'dealerName',
    value: dealerInfo.name,
    validator: val => validateRequiredString(val, 'Dealer name')
  }, {
    field: 'addressLine1',
    value: dealerInfo.addressLine1,
    validator: val => validateRequiredString(val, 'Address line 1')
  }, {
    field: 'city',
    value: dealerInfo.city,
    validator: val => validateRequiredString(val, 'City')
  }, {
    field: 'state',
    value: dealerInfo.state,
    validator: validateStateCode
  }, {
    field: 'zipCode',
    value: dealerInfo.zipCode,
    validator: validateZipCode
  }, {
    field: 'phoneNumber',
    value: dealerInfo.phoneNumber,
    validator: val => validateRequiredString(val, 'Phone number')
  }];
  processValidations(validations, errors, coerced);
  return {
    errors,
    coerced
  };
};

/**
 * Validates vehicle information
 * @param {Object} vehicleData - Vehicle data to validate
 * @returns {Object} Validation result with errors and coerced data
 */
const validateVehicleInfo = vehicleData => {
  const errors = [];
  const coerced = {};
  const validations = [
  // Required fields
  {
    field: 'year',
    value: vehicleData.year,
    validator: validateYear
  }, {
    field: 'vin',
    value: vehicleData.vin,
    validator: validateVin
  }, {
    field: 'photoUrl',
    value: vehicleData.photoUrl,
    validator: validatePhotoUrl
  }, {
    field: 'make',
    value: vehicleData.make,
    validator: val => validateRequiredString(val, 'Make')
  }, {
    field: 'model',
    value: vehicleData.model,
    validator: val => validateRequiredString(val, 'Model')
  }, {
    field: 'vehicleCondition',
    value: vehicleData.vehicleCondition,
    validator: val => validateRequiredString(val, 'Condition')
  }, {
    field: 'trim',
    value: vehicleData.trim,
    validator: val => validateRequiredString(val, 'Trim')
  }, {
    field: 'mileage',
    value: vehicleData.mileage,
    validator: val => validateRequiredNumber(val, 'Mileage', {
      min: 0
    })
  },
  // Optional fields
  {
    field: 'certifiedUsed',
    value: vehicleData.certifiedUsed,
    validator: val => validateOptionalString(val, 'Certified used')
  }, {
    field: 'chromeStyleId',
    value: vehicleData.chromeStyleId,
    validator: val => validateOptionalString(val, 'Chrome style ID')
  }, {
    field: 'interiorColor',
    value: vehicleData.interiorColor,
    validator: val => validateOptionalString(val, 'Interior color')
  }, {
    field: 'exteriorColor',
    value: vehicleData.exteriorColor,
    validator: val => validateOptionalString(val, 'Exterior color')
  }, {
    field: 'stockNumber',
    value: vehicleData.stockNumber,
    validator: val => validateOptionalString(val, 'Stock number')
  }];
  processValidations(validations, errors, coerced);
  return {
    errors,
    coerced
  };
};

/**
 * Validates payment information
 * @param {Object} paymentData - Payment data to validate
 * @returns {Object} Validation result with errors and coerced data
 */
const validatePaymentInfo = paymentData => {
  const errors = [];
  const coerced = {};
  const validations = [
  // Required fields
  {
    field: 'apr',
    value: paymentData.apr,
    validator: val => validateRequiredNumber(val, 'APR', {
      min: 0
    })
  }, {
    field: 'downPayment',
    value: paymentData.downPayment,
    validator: val => validateRequiredNumber(val, 'Down payment', {
      min: 0
    })
  }, {
    field: 'loanAmount',
    value: paymentData.loanAmount,
    validator: val => validateRequiredNumber(val, 'Loan amount', {
      min: 0
    })
  }, {
    field: 'price',
    value: paymentData.price,
    validator: val => validateRequiredNumber(val, 'Price', {
      requirePositive: true
    })
  },
  // Optional fields
  {
    field: 'creditTier',
    value: paymentData.creditTier,
    validator: val => validateOptionalString(val, 'Credit tier')
  }, {
    field: 'dueAtSign',
    value: paymentData.dueAtSign,
    validator: val => validateOptionalNumber(val, 'Due at signing', {
      min: 0
    })
  }, {
    field: 'estimatedPayment',
    value: paymentData.estimatedPayment,
    validator: val => validateOptionalNumber(val, 'Estimated payment', {
      min: 0
    })
  }, {
    field: 'msrp',
    value: paymentData.msrp,
    validator: val => validateOptionalNumber(val, 'MSRP', {
      min: 0
    })
  }, {
    field: 'netTrade',
    value: paymentData.netTrade,
    validator: val => validateOptionalNumber(val, 'Net trade')
  }, {
    field: 'rebate',
    value: paymentData.rebate,
    validator: val => validateOptionalNumber(val, 'Rebate', {
      min: 0
    })
  }, {
    field: 'selfReportedCredit',
    value: paymentData.selfReportedCredit,
    validator: val => validateOptionalNumber(val, 'Self-reported credit', {
      min: 0
    })
  }, {
    field: 'term',
    value: paymentData.term,
    validator: val => validateOptionalNumber(val, 'Term', {
      min: 1
    })
  }];
  processValidations(validations, errors, coerced);
  return {
    errors,
    coerced
  };
};
export { processValidations, validateDealerInfo, validateOptionalNumber, validateOptionalString, validatePaymentInfo, validatePhotoUrl, validateRequiredNumber, validateRequiredString, validateStateCode, validateVehicleInfo, validateVin, validateYear, validateZipCode };
